/* =============================================================================
   Safegear compare - selection UI - Horizon 4.1.3
   -----------------------------------------------------------------------------
   Injects a compare control onto every product card, stores the selection in
   localStorage, and renders a floating bar linking to /pages/compare.

   Config comes from snippets/sg-compare-globals.liquid, rendered once via
   snippets/custom.globals.liquid.

   TWO SOURCES OF CONTROLS, ONE SET OF BEHAVIOUR. Cards get a control injected by
   this file. The product page gets one rendered server-side by
   blocks/custom-compare-toggle.liquid, because a product page contains no
   <product-card> for the product being viewed - there is nothing to inject into.
   Everything after creation is shared: change and click are delegated from
   document, so a control's origin makes no difference to it. The only accommodation
   is that a server-rendered control needs its label and checked state filled at
   init - see hydrate() and syncBoxes().

   NO VENDOR FILE IS EDITED. The obvious route is adding markup to
   snippets/product-card.liquid, but that file is one of only three in-place
   vendor edits on this theme and already carries the CFP quick-add suppression.
   Every addition deepens the merge burden at the next upgrade. Compare depends
   on localStorage regardless, so a no-JS fallback was never available and the
   injection route costs nothing that server rendering would have preserved.

   HANDLE AND TITLE COME FROM THE CARD LINK, NOT FROM data-product-id.
   <product-card> exposes data-product-id, but the compare URL needs the HANDLE.
   The stretched link inside carries href="{{ product.url }}" and
   aria-label="{{ product.title }}", which supplies both. On a localised
   storefront product.url is already prefixed (/de/products/...), so the handle
   is the segment after '/products/'.

   Z-INDEX IS FUNCTIONALLY REQUIRED, NOT COSMETIC. .product-card__link is
   position:absolute inset:0 - a stretched link covering the whole card. Without
   a stacking context above it the control cannot be clicked at all. The minimum
   needed to make it work is set here; everything else (size, colour, placement
   polish) belongs in assets/custom.css section 11 and is deliberately absent.

   MUTATIONOBSERVER IS JUSTIFIED, NOT REFLEXIVE. facets.js replaces the product
   grid wholesale on every filter change, so injected controls vanish. Horizon
   publishes no re-render event to hook: assets/events.js defines only
   media:started-playing, quantity-selector:update, megaMenu:hover and
   zoom-media:selected. It was checked before reaching for observation. If a
   re-render event is ever added upstream, replace this.
   ============================================================================= */

(function () {
  'use strict';

  var KEY = 'sgCompare';
  var HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,199}$/;
  var cfg = null;

  /* ---- state ------------------------------------------------------------- */

  /* localStorage can throw: Safari private mode, disabled storage, quota. A
     compare selection is not worth breaking a collection page over, so every
     access degrades to an empty list rather than propagating. */
  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw.filter(function (item) {
        return item && typeof item.handle === 'string' && HANDLE_RE.test(item.handle);
      });
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* selection simply will not persist */
    }
  }

  function isSelected(handle) {
    return read().some(function (i) {
      return i.handle === handle;
    });
  }

  function toggle(handle, title) {
    var list = read();
    var at = -1;
    list.forEach(function (i, idx) {
      if (i.handle === handle) at = idx;
    });

    if (at !== -1) {
      list.splice(at, 1);
      write(list);
      return true;
    }
    if (list.length >= cfg.max) return false; // caller shows the limit message
    list.push({ handle: handle, title: title });
    write(list);
    return true;
  }

  /* ---- card parsing ------------------------------------------------------ */

  function handleFrom(card) {
    var link = card.querySelector('a.product-card__link[href]');
    if (!link) return null;
    var path = link.getAttribute('href').split('?')[0].split('#')[0];
    var marker = '/products/';
    var at = path.indexOf(marker);
    if (at === -1) return null;
    var handle = path.slice(at + marker.length).split('/')[0].toLowerCase();
    return HANDLE_RE.test(handle) ? handle : null;
  }

  function titleFrom(card) {
    var link = card.querySelector('a.product-card__link[aria-label]');
    return link ? link.getAttribute('aria-label') : '';
  }

  /* ---- injection --------------------------------------------------------- */

  function inject(card) {
    if (card.querySelector('[data-sg-compare-toggle]')) return;
    var handle = handleFrom(card);
    if (!handle) return; // placeholder card, or markup we do not recognise

    var wrap = document.createElement('label');
    /* --card mirrors --product on the server-rendered control in
       blocks/custom-compare-toggle.liquid. The base class carries everything the
       two variants share; anything that DIFFERS is scoped to one modifier or the
       other, and margin is currently the only such thing - the two controls sit at
       opposite ends of their containers, so their spacing is on opposite edges.
       The base rule in assets/custom.css section 11 therefore carries no margin at
       all. That is not tidiness: while the base rule held a margin-block-end, the
       product-page control silently inherited it despite its own comment block
       claiming it had no margin, and the same coupling put a stray top margin on
       it on 2026.08.02. Neither variant can now inherit the other's spacing. */
    wrap.className = 'sg-compare-select sg-compare-select--card';
    wrap.setAttribute('data-sg-compare-toggle', '');
    wrap.setAttribute('data-handle', handle);
    /* Functional minimum only - see the header note. position establishes the
       stacking context that z-index needs; without BOTH, the stretched link
       covering the card swallows every click on the control. Size, spacing and
       colour are in assets/custom.css section 11 and must stay there. */
    wrap.style.position = 'relative';
    wrap.style.zIndex = '2';

    var box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = isSelected(handle);

    var text = document.createElement('span');
    text.textContent = cfg.labelAdd;

    wrap.appendChild(box);
    wrap.appendChild(text);

    /* IN FLOW, AT THE END OF THE CARD CONTENT - NOT ABSOLUTELY POSITIONED OVER IT.
       The control was originally appended to <product-card> at top:8px left:8px.
       That assumes the first thing in the card is an image, and it is not always:
       the card's children are whichever blocks the template configures, so on any
       card with no media, or with the title above the media, the control landed on
       the title. There is no reliable gallery element to anchor to either -
       snippets/product-card.liquid renders `{{ children }}` and names nothing.

       .product-card__content IS emitted unconditionally by that snippet, so it is
       a stable target. The card itself remains the fallback.

       LAST, NOT FIRST. Safegear, 2026.08.02. Inserted first, the control rendered
       above its own card's media, which put it directly BENEATH the preceding row -
       and on the l2-collection template, directly beneath the Safe Series card,
       where it read as an offer to compare series rather than products. Appending
       puts it after the price, where it can only be read as belonging to the card
       it is inside. Both ends are equally safe from the overlap problem described
       above: that was solved by putting the control in flow rather than positioning
       it, and that part is unchanged. */
    var content = card.querySelector('.product-card__content') || card;
    content.appendChild(wrap);
  }

  function injectAll(scope) {
    (scope || document).querySelectorAll('product-card').forEach(inject);
  }

  function syncBoxes() {
    document.querySelectorAll('[data-sg-compare-toggle]').forEach(function (el) {
      var box = el.querySelector('input[type="checkbox"]');
      if (box) box.checked = isSelected(el.getAttribute('data-handle'));
    });
  }

  /* Server-rendered controls arrive with an EMPTY label span and are filled here.

     WHY THE BLOCK DOES NOT WRITE ITS OWN LABEL. A block text setting would be
     translatable, which is right in principle, but T-LAB is frozen, so it would
     render "Compare" in every locale. A second hardcoded locale map in the block
     would be the sixth on this theme and would have to be kept in step with the
     one in snippets/sg-compare-globals.liquid by hand. Filling from cfg.labelAdd
     keeps a single source for the string: the block renders the structure, the
     config node supplies the word. */
  function hydrate() {
    document.querySelectorAll('[data-sg-compare-label]').forEach(function (el) {
      if (!el.textContent.trim()) el.textContent = cfg.labelAddProduct;
    });
  }

  /* ---- floating bar ------------------------------------------------------ */

  function bar() {
    var el = document.querySelector('[data-sg-compare-bar]');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'sg-compare-bar';
    el.setAttribute('data-sg-compare-bar', '');
    /* Functional minimum only, as above. The insets that pin the bar to the foot
       of the viewport were here too; they are placement, not function, and now
       live in assets/custom.css section 11 with the rest of its appearance. */
    el.style.position = 'fixed';
    el.style.zIndex = '30';
    document.body.appendChild(el);
    return el;
  }

  function renderBar() {
    var list = read();
    var el = bar();

    if (!list.length) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = '';

    list.forEach(function (item) {
      var chip = document.createElement('span');
      chip.className = 'sg-compare-bar__chip';
      /* textContent, never innerHTML: titles are merchant data. */
      chip.textContent = item.title || item.handle;
      el.appendChild(chip);
    });

    var go = document.createElement('a');
    go.className = 'sg-compare-bar__go';
    go.href = cfg.compareUrl + '?items=' + list.map(function (i) {
      return encodeURIComponent(i.handle);
    }).join(',');
    go.textContent = cfg.labelCompare + ' (' + list.length + ')';
    el.appendChild(go);

    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'sg-compare-bar__clear';
    clear.setAttribute('data-sg-compare-clear', '');
    clear.textContent = cfg.labelClear;
    el.appendChild(clear);
  }

  /* ---- events ------------------------------------------------------------ */

  /* Delegated from document, so controls added by a later re-render are covered
     without rebinding. */
  function onChange(event) {
    var wrap = event.target.closest && event.target.closest('[data-sg-compare-toggle]');
    if (!wrap) return;
    var handle = wrap.getAttribute('data-handle');
    /* data-title first. The server-rendered control on the product page
       (blocks/custom-compare-toggle.liquid) carries it, and there is no
       <product-card> anywhere on that page to fall back to - without this the bar
       chip would show the handle instead of the product name. Injected controls on
       cards carry no data-title and still read it from the card. */
    var title = wrap.getAttribute('data-title');
    if (!title) {
      var card = wrap.closest('product-card');
      title = card ? titleFrom(card) : '';
    }
    var ok = toggle(handle, title);
    if (!ok) {
      event.target.checked = false;
      window.alert(cfg.labelLimit);
      return;
    }
    syncBoxes();
    renderBar();
  }

  function onClick(event) {
    if (!event.target.closest) return;
    /* The control sits inside the card's stretched link. Stop the click
       reaching it, or ticking the box navigates to the product. */
    if (event.target.closest('[data-sg-compare-toggle]')) {
      event.stopPropagation();
      return;
    }
    if (event.target.closest('[data-sg-compare-clear]')) {
      write([]);
      syncBoxes();
      renderBar();
    }
  }

  function init() {
    var node = document.querySelector('[data-sg-compare-config]');
    if (!node) return;
    cfg = {
      compareUrl: node.getAttribute('data-compare-url') || '/pages/compare',
      max: parseInt(node.getAttribute('data-max'), 10) || 3,
      labelCompare: node.getAttribute('data-label-compare') || 'Compare',
      labelClear: node.getAttribute('data-label-clear') || 'Clear',
      labelLimit: node.getAttribute('data-label-limit') || '',
      labelAdd: node.getAttribute('data-label-add') || 'Compare',
      /* The product page uses a longer form - see the note in
         snippets/sg-compare-globals.liquid. Falls back to the card string, so an
         older config node still produces a labelled control rather than a blank
         one. */
      labelAddProduct: node.getAttribute('data-label-add-product') ||
        node.getAttribute('data-label-add') || 'Compare'
    };

    injectAll();
    /* Injected controls are born with their label and checked state already set.
       Server-rendered ones are not, so both run here - hydrate for the word,
       syncBoxes for the tick. */
    hydrate();
    syncBoxes();
    renderBar();

    document.addEventListener('change', onChange);
    document.addEventListener('click', onClick, true);

    /* Re-inject after a facet re-render. See the header note on why this is an
       observer and not an event listener. */
    if (typeof MutationObserver === 'function') {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          injectAll();
          /* hydrate() BELONGS HERE, NOT ONLY IN init(). A server-rendered control
             that appears AFTER init has run - which is exactly what the theme
             editor does when a block is added, and what any section re-render
             does - would otherwise keep its empty label forever. Found on the
             product page, 2026.08.02: the block rendered, the checkbox worked,
             and the word next to it never arrived. */
          hydrate();
          syncBoxes();
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
